import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    HeightRule,
    TableLayoutType,
    ImageRun,
    AlignmentType,
    VerticalAlign,
    HorizontalPositionRelativeFrom,
    VerticalPositionRelativeFrom,
    TextWrappingType,
    HorizontalPositionAlign,
    VerticalPositionAlign
} from "docx";
import { hexToRGBA, FULL_WIDTH, SCALE, fixColor } from "@/utils/wordGenerator/common.js";

const generateLPTemp4HeaderImage = async (boxColor, logoUrl) => {
    return new Promise((resolve) => {
        try {
            const canvas = document.createElement('canvas');
            const WIDTH = 1600;
            const HEIGHT = 120;
            canvas.width = WIDTH;
            canvas.height = HEIGHT;
            const ctx = canvas.getContext('2d');

            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, WIDTH, HEIGHT);

            const grad = ctx.createLinearGradient(0, 0, 0, HEIGHT);
            grad.addColorStop(0, hexToRGBA(boxColor, 0.8));
            grad.addColorStop(1, hexToRGBA(boxColor, 0));
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, WIDTH, HEIGHT);

            if (logoUrl) {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    const logoBoxW = 350;
                    const logoBoxH = HEIGHT;
                    const padding = 15;

                    const drawAreaW = logoBoxW - (padding * 2);
                    const drawAreaH = logoBoxH - (padding * 2);

                    const scale = Math.min(drawAreaW / img.width, drawAreaH / img.height);
                    const w = img.width * scale;
                    const h = img.height * scale;

                    const x = (WIDTH - logoBoxW) + ((logoBoxW - w) / 2);
                    const y = (logoBoxH - h) / 2;

                    ctx.drawImage(img, x, y, w, h);
                    canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); });
                };
                img.onerror = () => { canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); }); };
                img.src = logoUrl;
            } else {
                canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); });
            }
        } catch (e) { resolve(null); }
    });
};

// Generate ONLY background image (photo + gradient, NO TEXT)
const generateLPTemp4BodyBgImage = async (proposal, bodyColor, footerColor) => {
    return new Promise(async (resolve) => {
        try {
            const canvas = document.createElement('canvas');
            const WIDTH = 1600;
            const HEIGHT = 2090;
            canvas.width = WIDTH;
            canvas.height = HEIGHT;
            const ctx = canvas.getContext('2d');

            ctx.fillStyle = bodyColor;
            ctx.fillRect(0, 0, WIDTH, HEIGHT);

            // Project Photo
            const targetPhoto = proposal.projects?.[0]?.uploadedPhoto || proposal.projects?.[0]?.photoURL;
            if (targetPhoto) {
                const pImg = new Image();
                pImg.crossOrigin = "anonymous";
                await new Promise(r => {
                    pImg.onload = r;
                    pImg.onerror = r;
                    pImg.src = targetPhoto;
                });

                if (pImg.complete && pImg.naturalWidth > 0) {
                    ctx.drawImage(pImg, 0, 0, WIDTH, HEIGHT);
                }
            }

            // Footer Gradient ONLY (NO TEXT)
            const footerH = 550;
            const gradY = HEIGHT - footerH;

            const grad = ctx.createLinearGradient(0, HEIGHT, 0, gradY);
            grad.addColorStop(0, hexToRGBA(footerColor, 1));
            grad.addColorStop(0.3, hexToRGBA(footerColor, 0.9));
            grad.addColorStop(1, 'rgba(255, 255, 255, 0.5)');

            ctx.fillStyle = grad;
            ctx.fillRect(0, gradY, WIDTH, footerH);

            canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); });

        } catch (e) { resolve(null); }
    });
};

export const createLPTemp4 = async (proposal) => {
    const style = proposal.style || {};
    const headerColor = style.colorScheme?.header || "#e66e2d";
    const bodyColor = style.colorScheme?.body || "#e66e2d";
    const footerColor = style.colorScheme?.footer || "#e66e2d";

    const PAGE_HEIGHT = 16838;
    const HEADER_HEIGHT_DXA = 918;
    const SPACER_HEIGHT_DXA = 11000;
    const CONTENT_HEIGHT_DXA = PAGE_HEIGHT - HEADER_HEIGHT_DXA - SPACER_HEIGHT_DXA;

    // Helper for creating editable text
    const createText = (text, styleObj, defaultSize = 12, overrideColor = null) => {
        return new TextRun({
            text: text || "N/A",
            font: styleObj?.font || "Arial",
            size: (styleObj?.size || defaultSize) * SCALE,
            bold: styleObj?.bold,
            italics: styleObj?.italic,
            underline: styleObj?.underline,
            color: fixColor(overrideColor || styleObj?.color || "#FFFFFF")
        });
    };

    // 1. Header Image
    const headerImgData = await generateLPTemp4HeaderImage(headerColor, proposal.logo);
    let headerRun = null;
    if (headerImgData) {
        headerRun = new ImageRun({
            data: headerImgData,
            transformation: { width: FULL_WIDTH / 15, height: HEADER_HEIGHT_DXA / 15 }
        });
    }

    // 2. Body Background (floating)
    const bodyBgImgData = await generateLPTemp4BodyBgImage(proposal, bodyColor, footerColor);
    let bodyBgRun = null;
    if (bodyBgImgData) {
        bodyBgRun = new ImageRun({
            data: bodyBgImgData,
            transformation: { width: FULL_WIDTH / 15, height: (SPACER_HEIGHT_DXA + CONTENT_HEIGHT_DXA) / 15 },
            floating: {
                zIndex: 0,
                horizontalPosition: {
                    relative: HorizontalPositionRelativeFrom.PAGE,
                    align: HorizontalPositionAlign.CENTER,
                },
                verticalPosition: {
                    relative: VerticalPositionRelativeFrom.PAGE,
                    align: VerticalPositionAlign.TOP,
                    offset: HEADER_HEIGHT_DXA,
                },
                behindDocument: true,
                wrap: { type: TextWrappingType.BEHIND }
            }
        });
    }

    const mainTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE }, insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE } },
        rows: [
            // Header Row
            new TableRow({
                height: { value: HEADER_HEIGHT_DXA, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        margins: { top: 0, bottom: 0, left: 0, right: 0 },
                        children: headerRun ? [new Paragraph({
                            spacing: { before: 0, after: 0 },
                            children: [headerRun]
                        })] : []
                    })
                ]
            }),
            // Spacer Row (holds floating background)
            new TableRow({
                height: { value: SPACER_HEIGHT_DXA, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        children: [
                            new Paragraph({
                                children: bodyBgRun ? [bodyBgRun] : []
                            })
                        ]
                    })
                ]
            }),
            // Content Row (EDITABLE TEXT on top of background)
            new TableRow({
                height: { value: CONTENT_HEIGHT_DXA, rule: HeightRule.AT_LEAST },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.BOTTOM,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 150 },
                                children: [createText(proposal.name, style.label, 32)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 50 },
                                children: [createText(proposal.address, style.text, 24)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 50 },
                                children: [createText(`${proposal.city || 'N/A'}, ${proposal.stateProvince || 'N/A'}`, style.text, 24)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 50 },
                                children: [createText(proposal.contactNo, style.text, 24)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 0 },
                                children: [createText(proposal.email, style.text, 24)]
                            }),
                            // Bottom spacing paragraph
                            new Paragraph({
                                spacing: { after: 400 },
                                children: []
                            })
                        ]
                    })
                ]
            })
        ]
    });

    return {
        properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
        children: [mainTable]
    };
};
